import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
    AdminCompanyEditResponseType, AdminRecruitmentEditResponseType,
    AdminRecruitmentRegisterType,
    AdminRecruitmentType
} from '../../../types/AdminTypes';
import {
  deleteAdminRecruitmentList,
  editAdminRecruitmentList,
  getAdminRecruitment,
  registerAdminRecruitment,
} from '../../../repository/AdminRepository';
import S from '../RecruitmentEditor/RecruitmentEditor.module.scss';
import AdminSearchSelectPopup from '../AdminSearchSelectPopup';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import {ErrorType} from "../../../types/ErrorType";

interface RecruitmentEditorProps {
  mode: 'register' | 'editor';
  setShotRecruitmentEditor: Dispatch<SetStateAction<boolean>>;
  id?: number;
}

const RecruitmentEditor = (props: RecruitmentEditorProps) => {
    const {mode, setShotRecruitmentEditor, id} = props;
    const ADMIN_TOKEN    = process.env.REACT_APP_IVY_ADMIN_TOKEN || '';
    const [recruitmentInfo, setRecruitmentInfo] = useState<AdminRecruitmentRegisterType>({} as AdminRecruitmentRegisterType);

    const handleChange = (name: string, value: any) => {
        setRecruitmentInfo((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleChangeBySelect = (type: 'regionId' | 'occupationId' | 'companyId', data: number) => {
        setRecruitmentInfo((prev) => ({
          ...prev,
          [type]: data
        }))
    }
    const handleSubmit = (mode: 'register' | 'edit' | 'delete') => {
        if (mode === 'register') {
            registerAdminRecruitment(recruitmentInfo, ADMIN_TOKEN)
                .then((res) => {
                    if ((res as AdminRecruitmentEditResponseType).success !== undefined) {
                        setRecruitmentInfo({} as AdminRecruitmentRegisterType);
                        setShotRecruitmentEditor(false);
                    }else{
                        alert((res as ErrorType).message);
                    }
                })
                .catch((e) => console.error(e));
        } else if (mode === 'edit') {
            if (id !== undefined) {
                editAdminRecruitmentList(id.toString(), recruitmentInfo, ADMIN_TOKEN)
                    .then((res) => {
                        if (res.success) {
                            setRecruitmentInfo({} as AdminRecruitmentRegisterType);
                            setShotRecruitmentEditor(false);
                        }
                    })
                    .catch((e) => console.error(e));
            }
        } else if (mode === 'delete') {
            if (id !== undefined) {
                deleteAdminRecruitmentList(id.toString(), ADMIN_TOKEN)
                    .then((res) => {
                        if (res.success) {
                            setRecruitmentInfo({} as AdminRecruitmentRegisterType);
                            setShotRecruitmentEditor(false);
                        }
                    })
                    .catch((e) => console.error(e));
            }
        }
    };

    const convertAndSetRecruitmentInfo = (recruitmentData: AdminRecruitmentType) => {
        const convertedData: AdminRecruitmentRegisterType = {
            title: recruitmentData.title,
            description: recruitmentData.description,
            regionId: recruitmentData.region.id,
            companyId: recruitmentData.company.id,
            occupationId: recruitmentData.occupation.id,
            salaryType: recruitmentData.salaryType,
            salary: recruitmentData.salaryDetail, // 이 예제에서는 salaryDetail을 salary로 매핑했습니다.
            salaryDetail: recruitmentData.salaryDetail,
            retirePay: recruitmentData.retirePay,
            insurance: recruitmentData.insurance,
            workType: recruitmentData.workType,
            startAt: recruitmentData.startAt,
            endAt: recruitmentData.endAt,
            education: recruitmentData.education,
            minEducation: recruitmentData.minEducation,
            maxEducation: recruitmentData.maxEducation,
            major: recruitmentData.major,
            certificate: recruitmentData.certificate,
            pfCond: recruitmentData.pfCond,
            etcPfCond: recruitmentData.etcPfCond,
            language: recruitmentData.language,
            career: recruitmentData.career,
            source: recruitmentData.source as 'INTERNAL' | 'EXTERNAL', // source 타입이 명확하기 때문에 타입 단언을 사용
            jobLink: recruitmentData.jobLink,
            screeningMethod: recruitmentData.screeningMethod,
            receiptMethod: recruitmentData.receiptMethod,
            submitDocumentation: recruitmentData.submitDocumentation,
            otherInformation: recruitmentData.otherInformation,
            attachFiles: recruitmentData.attachFiles,
            corpAttachFiles: recruitmentData.corpAttachFiles,
            zipCode: recruitmentData.zipCode,
            basicAddress: recruitmentData.basicAddress,
            detailAddress: recruitmentData.detailAddress,
            phoneNumber: recruitmentData.phoneNumber,
            faxNumber: recruitmentData.faxNumber,
            closed: recruitmentData.closed,
            modifyAt: recruitmentData.modifyAt,
            registerAt: recruitmentData.registerAt,
            displayScore: recruitmentData.displayScore
        };

        return convertedData;
    };

    useEffect(() => {
        if (id !== undefined && mode === 'editor') {
            getAdminRecruitment(id.toString(), ADMIN_TOKEN)
                .then((res) => {
                    if (res) {
                        setRecruitmentInfo(convertAndSetRecruitmentInfo(res));
                    }
                })
                .catch((e) => console.error(e));
        }
    }, [id]);

    return (
        <div className={S.recruitmentRegistration}>
            <div className={S.backButtonContainer}>
                <button type="button" onClick={() => setShotRecruitmentEditor(false)} className={S.backButton}>
                    X
                </button>
            </div>
            <RecruitmentRegisterField
                name={'title'}
                type={'input'}
                label={'공고 명'}
                value={recruitmentInfo.title || ''}
                changeFn={handleChange}
                required={true}
            />
            <RecruitmentRegisterField
                name={'description'}
                type={'textarea'}
                label={'공고 설명'}
                value={recruitmentInfo.description || ''}
                changeFn={handleChange}
                required={true}
            />
            <RecruitmentRegisterField
                name={'regionId'}
                type={'search'}
                searchType={'region'}
                label={'지역 id'}
                value={recruitmentInfo.regionId?.toString() || ''}
                changeFn={handleChangeBySelect}
                required={true}
            />
            <RecruitmentRegisterField
                name={'companyId'}
                type={'search'}
                searchType={'company'}
                label={'회사 id'}
                value={recruitmentInfo.companyId?.toString() || ''}
                changeFn={handleChangeBySelect}
                required={true}
            />
            <RecruitmentRegisterField
                name={'occupationId'}
                type={'search'}
                searchType={'occupation'}
                label={'직종 id'}
                value={recruitmentInfo.occupationId?.toString() || ''}
                changeFn={handleChangeBySelect}
                required={true}
            />
            <RecruitmentRegisterField
                name={'salaryType'}
                type={'select'}
                label={'임금 형태'}
                value={recruitmentInfo.salaryType || ''}
                options={[{label: '', value: ''}, {label: '연봉', value: '연봉'}, {label: '월급', value: '월급'}, {label: '시급', value: '시급'}]}
                changeFn={handleChange}
                required={true}
            />
            <RecruitmentRegisterField
                name={'source'}
                type={'select'}
                label={'채용 출처'}
                value={recruitmentInfo.source || ''}
                options={[{label: '', value: ''}, {label: 'INTERNAL', value: 'INTERNAL'}, {label: 'WORK_NET', value: 'WORK_NET'}]}
                changeFn={handleChange}
                required={true}
            />
            <RecruitmentRegisterField
                name={'closed'}
                type={'select'}
                label={'마감여부'}
                value={String(recruitmentInfo.closed) || 'false'}
                options={[{label: '', value: ''}, {label: 'false', value: false}, {label: 'true', value: true}]}
                changeFn={handleChange}
                required={true}
            />
            <RecruitmentRegisterField
                name={'displayScore'}
                type={'input'}
                label={'노출 score'}
                value={recruitmentInfo.displayScore?.toString()}
                changeFn={handleChangeBySelect}
                required={false}
            />
            <RecruitmentRegisterField
                name={'salary'}
                type={'input'}
                label={'임금'}
                value={recruitmentInfo.salary || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'salaryDetail'}
                type={'input'}
                label={'임금 상세'}
                value={recruitmentInfo.salaryDetail || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'retirePay'}
                type={'input'}
                label={'퇴직금 정보'}
                value={recruitmentInfo.retirePay || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'insurance'}
                type={'input'}
                label={'보험'}
                value={recruitmentInfo.insurance || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'workType'}
                type={'input'}
                label={'근무 형태'}
                value={recruitmentInfo.workType || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'startAt'}
                type={'datepicker'}
                label={'채용 시작일'}
                value={recruitmentInfo.startAt == null ? '':dayjs(recruitmentInfo.startAt || '').toDate()}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'endAt'}
                type={'datepicker'}
                label={'채용 종료일'}
                value={recruitmentInfo.endAt == null ? '':dayjs(recruitmentInfo.endAt || '').toDate()}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'education'}
                type={'input'}
                label={'학력'}
                value={recruitmentInfo.education || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'minEducation'}
                type={'input'}
                label={'최소 학력'}
                value={recruitmentInfo.minEducation || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'maxEducation'}
                type={'input'}
                label={'최대 학력'}
                value={recruitmentInfo.maxEducation || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'major'}
                type={'input'}
                label={'전공'}
                value={recruitmentInfo.major || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'certificate'}
                type={'input'}
                label={'요구 자격'}
                value={recruitmentInfo.certificate || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'pfCond'}
                type={'input'}
                label={'우대 조건'}
                value={recruitmentInfo.pfCond || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'etcPfCond'}
                type={'input'}
                label={'기타 우대 조건'}
                value={recruitmentInfo.etcPfCond || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'language'}
                type={'input'}
                label={'외국어 능력'}
                value={recruitmentInfo.language || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'career'}
                type={'input'}
                label={'경력'}
                value={recruitmentInfo.career || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'jobLink'}
                type={'input'}
                label={'채용 관련 링크'}
                value={recruitmentInfo.jobLink || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'screeningMethod'}
                type={'input'}
                label={'전형 방법'}
                value={recruitmentInfo.screeningMethod || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'receiptMethod'}
                type={'input'}
                label={'접수 방법'}
                value={recruitmentInfo.receiptMethod || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'submitDocumentation'}
                type={'input'}
                label={'제출 서류 준비물'}
                value={recruitmentInfo.submitDocumentation || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'otherInformation'}
                type={'input'}
                label={'기타 안내'}
                value={recruitmentInfo.otherInformation || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'zipCode'}
                type={'input'}
                label={'우편 번호'}
                value={recruitmentInfo.zipCode || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'basicAddress'}
                type={'input'}
                label={'기본 주소'}
                value={recruitmentInfo.basicAddress || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'detailAddress'}
                type={'input'}
                label={'상세 주소'}
                value={recruitmentInfo.detailAddress || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'phoneNumber'}
                type={'input'}
                label={'전화 번호'}
                value={recruitmentInfo.phoneNumber || ''}
                changeFn={handleChange}
                required={false}
            />
            <RecruitmentRegisterField
                name={'faxNumber'}
                type={'input'}
                label={'팩스 번호'}
                value={recruitmentInfo.faxNumber || ''}
                changeFn={handleChange}
                required={false}
            />

            {mode === 'register' && (
                <button className={S.editorButton} type="button" onClick={(e) => handleSubmit('register')}>
                    등록
                </button>
            )}
            {mode === 'editor' && (
                <div className={S.actionButtons}>
                    <button className={S.editButton} type="button" onClick={(e) => handleSubmit('edit')}>
                        수정
                    </button>
                    <button className={S.deleteButton} type="button" onClick={(e) => handleSubmit('delete')}>
                        삭제
                    </button>
                </div>
            )}
        </div>
    );
}

interface SelectOption {
    label: string;
    value: any;
}

interface RecruitmentRegisterFieldProps {
    name: string;
    type: 'input' | 'textarea' | 'search' | 'select' | 'datepicker'
    label: string;
    value: any;
    required: boolean;
    changeFn: Function;
    options?: SelectOption[];
    datepickerOptions?: object;
    searchType?: 'region' | 'company' | 'occupation';
}

const RecruitmentRegisterField = (props: RecruitmentRegisterFieldProps) => {
  const { name, type, label, value, changeFn, required, searchType, options, datepickerOptions } = props;
  const [showPopup, setShowPopup] = useState<boolean>(false);

    const handleChange = (e: any) => {
        if (type === 'datepicker' &&  e instanceof Date) {
            const isoDate = dayjs(e).toISOString();
            changeFn(name, isoDate); // datepicker는 다른 이벤트 객체를 가짐
        } else {
            changeFn(name, e.target.value);
        }
    };

  return (
    <div className={S.field}>
        {showPopup && searchType && (<AdminSearchSelectPopup searchType={searchType} onSelectFn={changeFn} setShowPopup={setShowPopup}/>)}
      <div className={S.labelContainer}>
        <div className={S.label}>{label}</div>
        {required && <span className={S.required}>*</span>}
      </div>
      {type === 'input' && (
        <input type="text" name={name} value={value} onChange={handleChange} />
      )}
      {type === 'textarea' && <textarea name={name} value={value} onChange={handleChange} />}
      {type === 'search' && (
          <div className={S.searchContainer}>
              <input type="text" name={name} value={value} readOnly onChange={handleChange}/>
              <button className={S.editorButton} onClick={() => setShowPopup(true)}>검색</button>
          </div>
      )}
        {type === 'select' && (
            <select name={name} value={value} onChange={handleChange}>
                {options?.map((option, index) => (
                    <option key={index} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        )}
        {type === 'datepicker' && (
            <DatePicker selected={value ? new Date(value) : undefined} onChange={handleChange} {...datepickerOptions} />
        )}
    </div>
  );
};

export default RecruitmentEditor;